// Core entity types for FEATit

import type { IAgentMergeTarget } from '../actors.js';
import {
  DASHBOARD_BLOCK_SPANS,
  type DashboardBlockSpanType,
  type DashboardRowHeightType,
} from '../page-layout.js';
import type { CommentKindType, IQuestionTurnView } from '../question-turn.js';
import type { INewItemBlockChip, NewItemBlockTextsType } from '../new-item-block-texts.js';
import type { IReplyPromise } from '../reply-promise.js';
import type { ICustomerSenderView } from '../customer-sender.js';
import type { ILocalePreferences } from '../i18n/preference.js';
import type { IPermissionHolderRole, PermissionType } from '../access-control.js';
import type { IShellRecentView, IToolbarPlace, ToolbarKeyType } from '../shell-bridge.js';
import type { IResolvedReference } from '../references.js';
import type { EstimateScaleType } from '../work-item-estimate.js';
import type { ICustomGroupingTextSpec } from '../view-engine/custom-groups.js';
import type { SelfJoinRoleType, SelfJoinScopeType } from '../self-registration.js';

/** Mail to the sign-in address is stopped (I-1395, I-1405): what the bounced-mail banner draws. */
export interface IMailStandingNotice {
  address: string;
  reason: 'bounced' | 'complained' | null;
  bouncedAgain: boolean;
  canSendAgain: boolean;
  retryAt: string | null;
}

/** An erasure waits out its grace period (F-0224 §6, I-1405): what the erasure banner draws. */
export interface IPendingDeletionNotice {
  requestedAt: string;
  scheduledFor: string;
  requestedBy: 'self' | 'system-admin';
}

export interface IUser {
  id: string;
  /**
   * The address this person **signs in** with, and the one that must be unique.
   *
   * ⚠️ Not necessarily the address FEATit writes to — see `contactEmail`. The two used to be one
   * field carrying two meanings, which is affordable right up until an address stops accepting
   * mail and there is nowhere to put the replacement without changing how somebody signs in.
   */
  email: string;
  /**
   * Where FEATit actually sends mail, when that is somewhere other than `email`.
   *
   * `null` means "use `email`" rather than "no address" — see `effectiveContactEmail`. A person
   * gets one of these when their sign-in address stops accepting messages: an Apple private relay
   * whose forwarding was switched off, a mailbox that was closed, an address that was never theirs.
   */
  contactEmail?: string | null;
  /**
   * Which sign-in provider supplied `email`, or `null` when the person typed it themselves.
   *
   * Provenance, not policy. When a warning cannot be delivered, the useful question is *what
   * address did we have and how did we get it* — and after the person has edited the address, the
   * identity links alone can no longer answer the second half.
   */
  emailProvidedBy?: OAuthProviderType | null;
  /** What the last delivery attempt to `effectiveContactEmail` did. Written by the mail path. */
  contactDeliveryStatus?: ContactDeliveryStatusType;
  /**
   * Whether this person should be asked for another address.
   *
   * True only for a **hard** failure. A deferral is a full mailbox or a bad afternoon and must not
   * produce a prompt, which is why the status has four values rather than a boolean.
   */
  needsAnotherContactEmail?: boolean;
  name: string;
  role: UserRoleType;
  avatarUrl?: string;
  deactivatedAt?: string | null;
  /**
   * Whether the sign-in address was ever proven reachable, and when (F-0160).
   *
   * `null` until an `EmailVerificationToken` is redeemed. A different fact from
   * `contactDeliveryStatus`: this is set once, at sign-up, and is never cleared by a later contact-
   * address change.
   */
  verifiedAt?: string | null;
  /** F-0554: the terms version this person last accepted, and when; `null` before any. */
  termsAcceptedVersion?: string | null;
  termsAcceptedAt?: string | null;
  /**
   * F-0554, AC-TA3: whether the terms in force must be accepted before the app works. Only `true`
   * blocks: an answer without the field (an older server, a fixture) is not asked.
   */
  termsAcceptanceRequired?: boolean;
  /**
   * I-1405: only `/auth/me` carries these two (the sign-in response does not). Each is the whole answer
   * of a shell banner, so the page knows from the session whether the bar is drawn and how tall it is
   * and the shell holds for it. `undefined` means this response did not ask: the banner asks itself.
   * `null` means asked and nothing to tell.
   */
  mailStanding?: IMailStandingNotice | null;
  pendingDeletion?: IPendingDeletionNotice | null;
  /**
   * I-1971: the person's own language and region, carried with their session (`/auth/me` and the sign-in
   * answers) so the page adopts it before any screen is drawn. `undefined` from an older server.
   */
  localePreferences?: ILocalePreferences;
  createdAt: string;
  updatedAt: string;
}

/**
 * The address FEATit writes to.
 *
 * One function so that "which address" has one answer everywhere. A second place that decides this
 * is a second place that can disagree with the first about who got the warning.
 */
export function effectiveContactEmail(user: Pick<IUser, 'email' | 'contactEmail'>): string {
  const contact = user.contactEmail?.trim();
  return contact && contact.length > 0 ? contact : user.email;
}

/**
 * The canonical form of a sign-in address — the only form `users.email` is ever stored in.
 *
 * ## Why this exists
 *
 * Without it, `Person@example.com` and `person@example.com` are two rows the unique index is
 * perfectly happy with: two accounts for one human, each holding half their work. Password sign-in
 * survives that by accident, because a person types the same thing they typed before. OAuth does
 * not — the address arrives from a provider in whatever case *the provider* uses, so the refusal
 * that protects an existing account (`ACCOUNT_EXISTS`) would miss it and create the duplicate.
 *
 * ## ⛔ ASCII case only, and that is the decision rather than an oversight
 *
 * The obvious implementation is `email.trim().toLowerCase()`. It is wrong here, and subtly: this
 * rule has to hold in **two languages at once**, because the database enforces it as a CHECK
 * constraint (`CHK_users_email_is_canonical`). `String.prototype.toLowerCase` and Postgres
 * `lower()` are not the same function — they disagree on locale-sensitive characters, Turkish
 * `İ` being the standard example — and a constraint the application can fail to satisfy turns a
 * valid sign-up into a 500 that names a column nobody was looking at.
 *
 * Folding only `A`–`Z` is expressible identically in both: here as a character-range replace,
 * there as `email !~ '[A-Z]'`. The two renderings agree by construction rather than by testing,
 * and `sign-in-email.test.ts` asserts the agreement on inputs chosen to break it.
 *
 * ⚠️ **What this deliberately does not do.** `bob@Exämple.com` and `bob@exämple.com` remain two
 * addresses. That is a narrower hole than the one being closed, it needs a Unicode case-folding
 * decision on both sides to close properly, and no provider FEATit federates with issues addresses
 * in that shape today. See ADR-084.
 *
 * ⚠️ Nothing else is normalised. Gmail's dots and `+tags` are **not** collapsed: they are one
 * provider's routing convention, other providers deliver them as distinct mailboxes, and a product
 * that decides two addresses are the same person is a product that can merge two humans.
 */
export function normalizeSignInEmail(email: string): string {
  return email.trim().replace(/[A-Z]/g, (letter) => letter.toLowerCase());
}

/**
 * Is this address already in the form `normalizeSignInEmail` produces?
 *
 * The predicate the database's CHECK constraint states in SQL. Kept beside the function it checks
 * so a reader can see that the two say the same thing.
 */
export function isCanonicalSignInEmail(email: string): boolean {
  return email === normalizeSignInEmail(email);
}

/**
 * What the mail path learned about an address, in the vocabulary identity keeps.
 *
 * ⛔ Four values rather than a boolean, because the product owner's ruling — *"if an address does
 * not accept messages, we ask the user for an additional email"* — is only fair if a single bounce
 * cannot trigger it. `deferred` and `undeliverable` are the two halves of "it did not work", and
 * only the second is a reason to ask somebody for a new address.
 */
export const CONTACT_DELIVERY_STATUSES = {
  /** Nothing has been sent to this address yet, or it has just been changed. */
  UNKNOWN: 'unknown',
  /** The provider accepted it for delivery. */
  DELIVERED: 'delivered',
  /** A soft failure — greylisting, a full mailbox, a temporary outage. Retryable; not the person's problem. */
  DEFERRED: 'deferred',
  /** A hard failure — no such mailbox, the domain rejects us, the relay was switched off. */
  UNDELIVERABLE: 'undeliverable',
} as const;

export type ContactDeliveryStatusType =
  (typeof CONTACT_DELIVERY_STATUSES)[keyof typeof CONTACT_DELIVERY_STATUSES];

export const CONTACT_DELIVERY_STATUS_VALUES: readonly ContactDeliveryStatusType[] =
  Object.values(CONTACT_DELIVERY_STATUSES);

/**
 * The third-party identity providers a person may sign in with.
 *
 * Not an enum — `code/shared` carries none, and the values are what the database column, the route
 * segment and the provider's own documentation all spell.
 */
export const OAUTH_PROVIDERS = {
  GOOGLE: 'google',
  MICROSOFT: 'microsoft',
  APPLE: 'apple',
} as const;

export type OAuthProviderType = (typeof OAUTH_PROVIDERS)[keyof typeof OAUTH_PROVIDERS];

export const OAUTH_PROVIDER_VALUES: readonly OAuthProviderType[] = Object.values(OAUTH_PROVIDERS);

/** The provider's own name for itself, for a button and for a sentence about an account. */
export const OAUTH_PROVIDER_LABELS: Record<OAuthProviderType, string> = {
  google: 'Google',
  microsoft: 'Microsoft',
  apple: 'Apple',
};

/**
 * A third-party identity attached to a FEATit account, as its holder sees it.
 *
 * ⛔ Nothing token-shaped appears here or in the record behind it. FEATit calls no provider API
 * after sign-in, so a stored provider access token would be a secret kept for no reader.
 */
export interface IOAuthIdentityLink {
  provider: OAuthProviderType;
  /** The address the provider gave at the last sign-in, unchanged since. */
  providerEmail: string | null;
  /** Whether the provider vouched for that address being the person's. */
  emailVerifiedByProvider: boolean;
  linkedAt: string;
  lastAuthenticatedAt: string | null;
}

/**
 * T-0633 (F-0563 D3): how far back a password reset looks for passkeys and sign-in providers that
 * were added, and offers to remove them. Long enough to cover a takeover found weeks later.
 */
export const RECENTLY_ADDED_SIGN_IN_METHOD_DAYS = 30;

/** A way in added shortly before a password reset, offered for removal by the reset's review. */
export interface IRecentlyAddedSignInMethod {
  kind: 'passkey' | 'provider';
  /** The passkey's id, or the provider's key (`google`). */
  id: string;
  /** The passkey's name, or the provider's own name. */
  name: string;
  /** For a provider, the address the provider gave; for a passkey, null. */
  detail: string | null;
  addedAt: string;
}

/**
 * I-2003: what `POST /auth/password-reset/check` answers for the link a reset page opened with. An
 * expired, spent and unknown link are one state, as the confirm refusal is one sentence.
 */
export const PASSWORD_RESET_LINK_STATES = { USABLE: 'usable', NOT_USABLE: 'not-usable' } as const;
export type PasswordResetLinkStateType =
  (typeof PASSWORD_RESET_LINK_STATES)[keyof typeof PASSWORD_RESET_LINK_STATES];

/**
 * What a completed password reset answers (D3, D7). The grant removes a listed method for 15
 * minutes; it is null when nothing was added. ⛔ Never put it in a URL or in storage.
 */
export interface IPasswordResetReview {
  grant: string | null;
  expiresAt: string | null;
  recentlyAdded: IRecentlyAddedSignInMethod[];
}

/** Which providers a deployment has been configured for. An unconfigured one is never offered. */
export interface IOAuthAvailability {
  providers: OAuthProviderType[];
}

/**
 * Why a provider sign-in did not happen.
 *
 * ⛔ A code, not a message, because this value travels back in a redirect URL. A message written by
 * the backend would be rendered by whichever page received it, and a URL parameter rendered as text
 * is an injection surface; it would also be the natural place for somebody to interpolate a
 * provider's own response into.
 *
 * Here rather than in the backend so the sentence the web app shows and the code the backend sends
 * cannot drift apart — a refusal nobody wrote a sentence for renders as a bare slug on the sign-in
 * screen, which is the one screen where a person has no other way to find out what went wrong.
 */
export const OAUTH_REFUSALS = {
  /** The state was unknown, replayed, or older than its window. */
  EXPIRED: 'sign-in-expired',
  /** The provider refused, or the person cancelled at the provider. */
  PROVIDER_REFUSED: 'provider-refused',
  /** The provider sent no address at all. */
  NO_EMAIL: 'no-email',
  /** The provider would not vouch for the address, so no account may be created on it. */
  EMAIL_UNVERIFIED: 'email-unverified',
  /**
   * ⛔ The address already belongs to an account, and FEATit **never** auto-links on a matching
   * address — that is a documented account-takeover route. Connecting the provider is an
   * authenticated action taken from the account screen instead.
   */
  ACCOUNT_EXISTS: 'account-exists',
  /** This provider account already signs somebody else in. */
  IDENTITY_CLAIMED: 'identity-claimed',
  /** This account already has a different account connected for the same provider. */
  ALREADY_CONNECTED: 'already-connected',
  /** The account is deactivated. */
  DEACTIVATED: 'account-deactivated',
  /** The provider is not configured on this deployment. */
  NOT_CONFIGURED: 'provider-not-configured',
  /**
   * This deployment does not accept new accounts from that address — sign-up is closed, or limited
   * to approved email domains.
   *
   * ⚠️ One code for both, deliberately. Distinguishing them would tell an unauthenticated caller
   * which of the two rules they fell foul of, and "your domain is not on the list" is a statement
   * about the deployment's configuration made to somebody who has no account on it. Nobody whose
   * account already exists ever sees this: it is raised only on the branch that would create one.
   */
  REGISTRATION_CLOSED: 'registration-closed',
  /**
   * The deployment has no administrator yet, so nobody may create an account until its operator has
   * run setup (F-0160 AC-53, I-0638). Separate from `REGISTRATION_CLOSED` because the remedy is not
   * "ask an administrator" — there is none — and saying so reveals nothing about any address.
   */
  SETUP_REQUIRED: 'setup-required',
} as const;

export type OAuthRefusalType = (typeof OAUTH_REFUSALS)[keyof typeof OAUTH_REFUSALS];

export const OAUTH_REFUSAL_VALUES: readonly OAuthRefusalType[] = Object.values(OAUTH_REFUSALS);

export const USER_ROLES = {
  USER: 'user',
  TESTER: 'tester',
  PRODUCT_MANAGER: 'product_manager',
  DEVELOPER: 'developer',
  ADMIN: 'admin',
} as const;

export type UserRoleType = (typeof USER_ROLES)[keyof typeof USER_ROLES];

export interface IProject {
  id: string;
  name: string;
  slug: string;
  description?: string;
  policies?: Record<string, unknown>;
  parentId?: string | null;
  parent?: IProject | null;
  /**
   * Whether roles held on the parent work area reach this one (D-0054). Absent from an older server;
   * absent reads as inheriting, which is the column default.
   */
  inheritsAccess?: boolean;
  visibility?: WorkAreaVisibilityType;
  /**
   * I-1192 (§5p KL-15): whether this work area's text reads document references (`ADR-`, `AC-`,
   * `R-` …) against FEATit's bundled documentation index. Absent from an older server; absent reads
   * as off, the column default.
   */
  linkDocumentReferences?: boolean;
  /**
   * F-0704 (`docs/concept-examples.md` §3.4): the shipped example this work area was installed from;
   * null or absent for every other work area. Its header then says the content is demo data.
   */
  examplePackageKey?: string | null;
  /**
   * F-0782 (§3.8, EXA-31): whether this example work area is upgraded by itself when the server ships
   * a newer version of its package. Read only where `examplePackageKey` is set; absent from a server
   * before F-0782, and absent reads as on, the column default.
   */
  exampleAutoUpdate?: boolean;
  /** F-0704 (§3.2): the role self-registration gives a member of the organisation; null is off. */
  selfJoinRole?: SelfJoinRoleType | null;
  /**
   * F-0739 (PJ-1): who may use that self-registration: `organisation` (the default) or `signed_in`,
   * anyone with an account while the work area is Public. Absent from an older server.
   */
  selfJoinScope?: SelfJoinScopeType;
  /**
   * F-0737 (ownership concept §10.17, PW-R3): what the product made this work area as: `personal`
   * for the Personal work area every account starts with. Never cleared. Absent from an older server.
   */
  seedId?: string | null;
  /**
   * F-0737 (PW-R3, PW-R4): set while the name is still the product's, so it is drawn in the reader's
   * language (`workAreaNameIn`); null once the person renamed it, and `name` is then theirs as typed.
   */
  nameSeedId?: string | null;
  workAreaTypeId?: string | null;
  workAreaType?: IWorkAreaType | null;
  access?: IWorkAreaAccess;
  ownerId: string;
  /**
   * The namespace this work area lives in (R-3, F-0221). Optional because an older server does not
   * send it — a work area predating the namespace primitive, or a response this field has not
   * reached yet — never because a work area can lack one; every row has `namespace_id NOT NULL`.
   */
  namespaceId?: string;
  /** The `/`-joined full path of {@link namespaceId} — `acme`, `acme/platform` — for display and for a qualified `namespace/slug` reference. */
  namespace?: string | null;
  /** The namespace's display name, never empty (I-0657). For `Acme / Tracker` labels (AC-M14). */
  namespaceName?: string | null;
  /**
   * The namespace's immutable key (I-0681, ADR-113). `<key>/<slug>` names this work area whatever
   * the namespace's handle becomes, which is what a stored reference uses (AC-K9).
   */
  namespaceKey?: string | null;
  /**
   * Whether this work area is in the reader's own personal namespace (I-0656, AC-M18). A bare
   * reference answered by several work areas resolves to the reader's own when exactly one is.
   */
  namespaceIsOwn?: boolean;
  /**
   * PX-19 (D-0428 c): who a customer reads as the sender here, as the server reads it (the stored
   * `policies.customerSender`, else `agency` where a New item block switched `namePerson` off, else
   * `person`), with the named role's display name and never its key. A guest is told it too
   * (`GUEST_WORK_AREA_FIELDS`): their reply sentence names its sender by it.
   */
  customerSender?: ICustomerSenderView | null;
  /**
   * Addresses this work area answered to before a rename or a move, still held for it (I-1052; same
   * rule as the namespace handle hold). Absent, or empty, once every hold has expired. A caller
   * resolving a reference against a list of work areas flattens these into `IWorkAreaAddressHold`s
   * (`current` is this work area) and passes them to `resolveWorkAreaReference`'s `holds` parameter.
   */
  formerAddresses?: readonly IWorkAreaFormerAddress[];
  /** T-0502: how this work area's estimates read, or null for none; absent from an older server. */
  estimateScale?: EstimateScaleType | null;
  createdAt: string;
  updatedAt: string;
}

/** One vacated address a work area still answers to, in the form {@link IProject.formerAddresses} carries. */
export interface IWorkAreaFormerAddress {
  readonly namespace?: string;
  readonly namespaceKey?: string;
  readonly slug: string;
  readonly name: string;
}

/**
 * Who can read a work area. Three of ADR-150's four levels ship: Private and Organisation
 * (D-0176 answered **b**, Jay, 2026-09-30) and Public (F-0338, Jay, 2026-10-01: the demo work area
 * needs it before early access). Deployment comes after v1 (F-0337).
 *
 * - `private` — members of the work area, and people with a role on a namespace or work area above
 *   it. The default.
 * - `organisation` — every **member of the organisation that owns the work area** (the root of its
 *   namespace): a person who owns that namespace or holds a non-guest role anywhere in it. ⛔ Never
 *   the whole deployment (I-0922), never a guest (F-0271; ADR-150 §1), and never an agent in its
 *   own right — an agent reads through its own binding, capped by its holder (ADR-051).
 *
 * The level was spelled `internal` until F-0325, and before that `workspace`. `internal` read as
 * "everyone on this server" and was resolved that way, which let a stranger who signed up read every
 * organisation's work areas; the new spelling names the boundary it now enforces.
 *
 * - `public` — anyone who reaches the deployment, signed out included, **through the public read
 *   surface** (`backend/src/public-read`), read only (F-0338, concept §10.7a). ⛔ It is not a role and
 *   adds no implicit binding: the signed-in resolver treats it like Private, so a Public work area
 *   gives a member nothing more than membership already did, and every public read passes the one
 *   allow-listed projection. In the vocabulary, but **offered** only while a System Admin allows it,
 *   which is off by default ({@link offeredWorkAreaVisibilities}); then to a holder of
 *   `work_area.publish`, under a namespace chain that is itself publicly readable.
 *
 * ⛔ **No `deployment` yet.** ADR-150 §2 models it; F-0337 builds it after v1. A value nobody can
 * select reads to the next person as a capability the product does not have.
 */
export const WORK_AREA_VISIBILITIES = {
  PRIVATE: 'private',
  ORGANISATION: 'organisation',
  PUBLIC: 'public',
} as const;

/**
 * The values rows carried before each rename, kept **only** so the migrations and their specs can
 * name them without a bare literal.
 *
 * ⛔ Not members of `WORK_AREA_VISIBILITIES` and not assignable to `WorkAreaVisibilityType`: nothing
 * in the product may read, write or offer them. They are recorded history, and history is what a
 * migration needs a name for.
 */
export const WORK_AREA_VISIBILITY_RETIRED_WORKSPACE = 'workspace';
/** `internal`, which meant every account on the deployment until F-0325 made it `organisation`. */
export const WORK_AREA_VISIBILITY_RETIRED_INTERNAL = 'internal';

export type WorkAreaVisibilityType =
  (typeof WORK_AREA_VISIBILITIES)[keyof typeof WORK_AREA_VISIBILITIES];

/**
 * The levels this deployment **offers** — what a picker shows, what the API accepts and what the CLI
 * names — which is not the vocabulary above. D-0176 b holds by default: Private and Organisation only.
 * Public is offered only while a System Admin has allowed it deployment-wide (F-0338 AC-PV2), and that
 * switch is off until somebody turns it on — on the public cloud and on a company's own server alike,
 * and on a deployment that has never recorded a choice (`public-visibility-policy.ts`).
 *
 * The per-work-area conditions (the permission, the namespace chain, a takedown) come after this and
 * are said as a reason on a control that stays visible; this is the deployment's answer, which decides
 * whether the level exists here at all.
 */
export function offeredWorkAreaVisibilities(deployment: {
  readonly publicAllowed: boolean;
}): WorkAreaVisibilityType[] {
  const offered: WorkAreaVisibilityType[] = [
    WORK_AREA_VISIBILITIES.PRIVATE,
    WORK_AREA_VISIBILITIES.ORGANISATION,
  ];
  if (deployment.publicAllowed === true) offered.push(WORK_AREA_VISIBILITIES.PUBLIC);
  return offered;
}

/**
 * The audience order of ADR-150 §2 — Private < Organisation < Public (Deployment will sit between the
 * last two). A child is never more visible than its parent (AC-W8, AC-PV1), and "more visible" is
 * this number, never a comparison against Private alone: that is how a two-level rule waved an
 * Organisation child under a Private parent's sibling and would wave a Public one under an
 * Organisation parent.
 */
export const WORK_AREA_VISIBILITY_RANK: Readonly<Record<WorkAreaVisibilityType, number>> = {
  private: 0,
  organisation: 1,
  public: 2,
};

/** Whether a work area of `child` visibility may sit under a parent of `parent` visibility. */
export function visibilityFitsUnder(
  child: WorkAreaVisibilityType,
  parent: WorkAreaVisibilityType,
): boolean {
  return WORK_AREA_VISIBILITY_RANK[child] <= WORK_AREA_VISIBILITY_RANK[parent];
}

export interface IWorkAreaType {
  id: string;
  key: string;
  name: string;
  titleI18n: Record<string, string>;
  defaults: IWorkAreaTypeDefaults;
  position: number;
  isActive: boolean;
  isDefault: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface IWorkAreaTypeDefaults {
  visibility?: WorkAreaVisibilityType;
  policies?: Record<string, unknown>;
}

export type IWorkArea = IProject;

export interface IWorkAreaAccess {
  role: 'viewer' | 'contributor' | 'manager' | 'manager-demo' | 'admin' | 'owner';
  explicitMembership: boolean;
  canContribute: boolean;
  canManage: boolean;
  /**
   * Every permission this reader effectively holds here — bindings narrowed by the credential the
   * request came on (ADR-051), so a verification session's list lacks what its ceiling lacks.
   *
   * ⭐ What a screen asks instead of a role, a grade or a credential kind (I-0365; decision 3,
   * 2026-09-16): a role gains a control by being granted the permission, and no screen changes.
   * `canContribute` and `canManage` are two of these, pre-answered, and stay for callers not yet moved.
   *
   * Optional only because a server older than this field does not send it; see
   * `missingWorkAreaPermission` for what a reader does then.
   */
  permissions?: PermissionType[];
  /**
   * For each Owner-only permission (`OWNER_ONLY_PERMISSIONS`) this reader does **not** hold, the
   * roles that hold it here, so a refused control names who can (I-1462): `onlyHoldersCan`. Absent
   * from an older server, and the refusal then says only which permission it needs.
   */
  holders?: Partial<Record<PermissionType, IPermissionHolderRole[]>>;
  /**
   * F-0621 (AGM-41): the reader holds nothing here but a guest role or a page shared with them, so
   * the work area is listed only to resolve its address, as `guestWorkAreaListing` answers it, and
   * the web draws the guest's shell. Absent for every member.
   */
  guest?: boolean;
}

export const WORK_AREA_RESOURCE_KINDS = {
  LINK: 'link',
} as const;

export type WorkAreaResourceKindType =
  (typeof WORK_AREA_RESOURCE_KINDS)[keyof typeof WORK_AREA_RESOURCE_KINDS];

export interface IWorkAreaResource {
  id: string;
  projectId: string;
  title: string;
  url: string;
  description?: string | null;
  kind: WorkAreaResourceKindType;
  pinned: boolean;
  position: number;
  /** Null once the identity that created it has been deleted — the resource survives (ADR-045). */
  createdById: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface IWorkAreaResourceCheckpoint {
  count: number;
  updatedAt: string;
}

export interface IWorkAreaResourceSnapshot {
  items: IWorkAreaResource[];
  canManage: boolean;
  checkpoint: IWorkAreaResourceCheckpoint;
}

/**
 * Where a document lives: a page, a work item or a part (ADR-098; a part since F-0320). A document
 * has at least one place, and the same document on several places is one document — an edit shows
 * on all of them.
 */
export const DOCUMENT_PLACE_TYPES = {
  PAGE: 'page',
  ITEM: 'item',
  PART: 'part',
} as const;

export type DocumentPlaceType = (typeof DOCUMENT_PLACE_TYPES)[keyof typeof DOCUMENT_PLACE_TYPES];

export interface IWorkAreaDocumentPlace {
  /** The place's own id — what a detach names. */
  id: string;
  documentId: string;
  type: DocumentPlaceType;
  pageId: string | null;
  /** `true` for the work area's Home page, which every re-homed document falls back to. */
  isHome: boolean;
  workItemId: string | null;
  /** The item's key, e.g. `F-0187`; null for a page. */
  itemKey: string | null;
  /** The part, for a place of type `part` (F-0320); absent or null otherwise. */
  partId?: string | null;
  /** The part's handle, e.g. `desktop/bridge`, for a place of type `part`. */
  partHandle?: string | null;
  /** The page title or the item title. */
  title: string;
  /** The work area the place is in — an item may have moved since the document was attached. */
  workAreaId: string;
  position: number;
}

/** Where the document is attached to and hidden from the caller: counted, never named. */
export interface IWorkAreaDocumentPlaces {
  places: IWorkAreaDocumentPlace[];
  hiddenPlaceCount: number;
}

/** An agent that wrote a document, with the agent now holding its work when it was merged away (I-0314). */
export interface IWorkAreaDocumentAgent {
  id: string;
  displayName: string;
  mergedIntoAgent?: IAgentMergeTarget | null;
}

export interface IWorkAreaDocumentSummary extends IWorkAreaDocumentPlaces {
  id: string;
  projectId: string;
  title: string;
  /** Both null once the identity is deleted — the document survives its author (ADR-045). */
  createdById: string | null;
  updatedById: string | null;
  /**
   * I-0897: the agent the writing credential named — who created it, and who last wrote it — when
   * it named one; null for a person writing as themselves. Absent from a server before I-0897.
   */
  createdByAgent?: IWorkAreaDocumentAgent | null;
  updatedByAgent?: IWorkAreaDocumentAgent | null;
  /**
   * I-1398: the people behind createdById and updatedById, so a page can say "<agent>, for
   * <person>". Null once the identity is deleted. Absent from a server before I-1398.
   */
  createdByName?: string | null;
  updatedByName?: string | null;
  createdAt: string;
  updatedAt: string;
  /**
   * F-0464 (OF-58): the file this document is a copy of, or null for an ordinary document. A copy
   * is changed only by saving its file again (OF-59). Absent from a server before F-0464.
   */
  source?: IWorkAreaDocumentSource | null;
}

/** The file a document is a copy of (F-0464, OF-58). */
export interface IWorkAreaDocumentSource {
  /** The file's path from the checkout's root, with `/` between segments. */
  path: string;
  /** The full id of the commit the checkout held when the file was saved. */
  commit: string;
  /** When the copy was last saved, ISO 8601. */
  savedAt: string;
}

export interface IWorkAreaDocument extends IWorkAreaDocumentSummary {
  content: string;
  /** Write on any place it lives on. */
  canEdit: boolean;
  /** Write on every place it lives on, because deleting removes it from all of them. */
  canDelete: boolean;
}

/** The documents on one page or one item, in that place's order. */
export interface IDocumentPlaceListing {
  items: IWorkAreaDocumentSummary[];
  /** Whether the caller may add, attach or detach documents here. */
  canManage: boolean;
  /**
   * For a page: the ordered default document names in force for its work area, which pick the
   * page's readme (`firstDefaultDocument`). Absent for an item.
   */
  defaultDocumentNames?: string[];
}

/**
 * A work area's default document names (F-0187): its own list, and the list in force — its own, else
 * the nearest parent's, else `DEFAULT_DOCUMENT_NAMES` — with where that came from.
 */
export interface IWorkAreaDocumentNames {
  /** This work area's own list; `null` when it sets none and inherits. */
  own: string[] | null;
  effective: string[];
  source: 'work-area' | 'parent' | 'default';
  /** The work area whose list is in force; `null` for the built-in default. */
  sourceWorkAreaId: string | null;
  /** Whether the caller may set or clear the list (`work_area.manage`). */
  canManage: boolean;
}

/** At most this many names, each at most this long: a readme is picked from a short, typed list. */
export const DOCUMENT_DEFAULT_NAMES_MAX = 20;
export const DOCUMENT_DEFAULT_NAME_MAX_LENGTH = 200;

/** The names a page's readme block looks for, in order, when the work area names none (F-0187). */
export const DEFAULT_DOCUMENT_NAMES: readonly string[] = ['README.md', 'readme.md', 'index.md'];

/**
 * The page's readme: the first of `names`, in the order given, that a document's title matches
 * case-insensitively. A later name never beats an earlier one, whatever order the documents are in.
 * Documents carry a title and no file name, so the title is what is matched.
 */
export function firstDefaultDocument<T extends { title: string }>(
  documents: readonly T[],
  names: readonly string[],
): T | null {
  for (const name of names) {
    const wanted = name.trim().toLocaleLowerCase();
    if (!wanted) continue;
    const match = documents.find(
      (document) => document.title.trim().toLocaleLowerCase() === wanted,
    );
    if (match) return match;
  }
  return null;
}

/**
 * A list of README names as stored or typed: trimmed, blank and over-long names dropped, a name
 * repeated regardless of case kept once (matching ignores case), at most `DOCUMENT_DEFAULT_NAMES_MAX`.
 * `null` when nothing usable is left, so the caller inherits. The block-defaults resolver reads a work
 * area's names with it, and the server cleans a policies write with it (F-0195).
 */
export function parseDocumentNames(value: unknown): string[] | null {
  if (!Array.isArray(value)) return null;
  const names: string[] = [];
  const seen = new Set<string>();
  for (const entry of value) {
    if (typeof entry !== 'string') continue;
    const name = entry.trim();
    const key = name.toLocaleLowerCase();
    if (!name || name.length > DOCUMENT_DEFAULT_NAME_MAX_LENGTH || seen.has(key)) continue;
    seen.add(key);
    names.push(name);
  }
  return names.length > 0 ? names.slice(0, DOCUMENT_DEFAULT_NAMES_MAX) : null;
}

/**
 * A place a document lives on (ADR-098): a page, an item, or a part (F-0320, concept-parts §3.3).
 */
export type DocumentPlaceTarget =
  | { pageId: string }
  | { workItemId: string }
  | PartDocumentPlaceTarget;

/** A part as a document's place (F-0320, concept-parts §3.3). */
export type PartDocumentPlaceTarget = { partId: string };

export interface IWorkAreaDocumentCheckpoint {
  count: number;
  revision: number;
  updatedAt: string;
}

export interface IWorkAreaDocumentSnapshot {
  items: IWorkAreaDocumentSummary[];
  canManage: boolean;
  checkpoint: IWorkAreaDocumentCheckpoint;
}

export const DASHBOARD_BLOCK_TYPES = {
  ACTIVITY_OVERVIEW: 'activity-overview',
  WORK_ITEM_LIST: 'work-item-list',
  SAVED_VIEW: 'saved-view',
  ROADMAP: 'roadmap',
  DOCUMENT_LIST: 'document-list',
  DOCUMENT_PREVIEW: 'document-preview',
  /** The first document on the page whose title is one of the default names (README.md …). */
  PAGE_README: 'page-readme',
  /** The decisions waiting for the signed-in reader (F-0222) — the work area's, or every one. */
  DECISIONS: 'decisions',
  /** Everything but decisions waiting for the signed-in reader (I-0900) — the work area's, or every one. */
  TASKS: 'tasks',
  /**
   * The moves of one item, the page's scope item (F-0621, concept-design-agency §21.3, AGM-7). Each
   * is the item page's own move, through the same route and the same `work_item.update` check, so
   * a page offers nothing its reader could not do on the item itself.
   */
  ACTION_BAR: 'action-bar',
  /**
   * One file of the page's scope item, large, with its versions and the comments on each (F-0621,
   * concept-design-agency §21.3, §22.3): a version switcher, compare, zoom, and the comments on the
   * open version with those on earlier versions apart, never moved onto it (AGM-15, AGM-36).
   */
  REVIEW_CANVAS: 'review-canvas',
  /**
   * The scope item's current revision set and its approval (F-0621, §21.3, §22.3): Approve and
   * Request changes send the digest the reader saw, and a stale one is refused with what changed
   * (AGM-12, AGM-37). The item's own `work_item.update` check, asked by the route.
   */
  APPROVAL_PANEL: 'approval-panel',
  /**
   * One item's fields, limited to the block's field set (F-0621, concept-design-agency §21.3,
   * §22.1, AGM-44): never more than the reader's own read returns, a guest's through the allowlist.
   */
  ITEM_FIELDS: 'item-fields',
  /**
   * The reader's one next step on one item, derived from what waits for them: an approval, their
   * questions, or nothing (F-0621, §21.3, §22.1, AGM-45). Never from stored text.
   */
  NEXT_ACTION: 'next-action',
  /**
   * The files of one item and of its children as a contact sheet, grouped by item, each at its newest
   * version and whether the current revision set holds it (F-0621, concept-design-agency §21.3,
   * AGM-49). Read-only; a guest's children are only those shared with their role.
   */
  FILE_GALLERY: 'file-gallery',
  /**
   * The children of one item counted by state, compact (F-0621, concept-design-agency §21.3, §22.1,
   * AGM-53): a column per state with its count, a state with no child kept. A guest's are only the
   * children shared with their role, in reporter words.
   */
  COVERAGE_GRID: 'coverage-grid',
  /**
   * One item's trail as the item's Trail tab draws it (F-0621, concept-design-agency §21.3, AGM-55;
   * F-0618 v0). Read-only; a guest's is the trail as shared with their role.
   */
  TRAIL: 'trail',
  /**
   * The questions put to the reader on one item (F-0621, concept-design-agency §22.1, AGM-58): a
   * guest answers on the page through the shared-item route; a member is linked to the item.
   */
  ITEM_QUESTIONS: 'item-questions',
  /**
   * A form the reader files a new item of one type with, when the type's `guestFiling` names a guest
   * role they hold (F-0621, AGM-61): a title, a description and their role's field set.
   * Drawn disabled with the reason for anyone else, never hidden.
   */
  NEW_ITEM: 'new-item',
  /**
   * A customer's project as progress (PX-5, concept-design-agency §1.1 principle 6): its designs as
   * large previews of the version shared with the reader, one sentence of progress in outcomes,
   * who looks after it and when it is ready, then its journey as a timeline with today marked.
   * Read-only; a guest's designs are only those shared with their role.
   */
  PROJECT_PROGRESS: 'project-progress',
  /**
   * PX-16 (concept-design-agency §24.5, walkthrough A step 5): a member's grid of one item's designs
   * by two fields (format and language), leading with the ones the agent's checks flagged, each with
   * its reason in a sentence, and "Redo this one" on them. A design a person edited by hand is never
   * redone over (§10); the server refuses it. Members only.
   */
  DESIGN_GRID: 'design-grid',
} as const;

export type DashboardBlockType = (typeof DASHBOARD_BLOCK_TYPES)[keyof typeof DASHBOARD_BLOCK_TYPES];

/**
 * ⛔ **Retired by F-0203.** A block's width is now `span`, one of `DASHBOARD_BLOCK_SPANS` in
 * `page-layout.ts`, and the row it sits on is stated by `newRow` rather than inferred by a
 * two-column CSS grid. These two names survive only so the migration that converts stored blocks
 * can say what it is converting *from*; nothing renders or validates against them.
 */
export const LEGACY_DASHBOARD_BLOCK_WIDTHS = {
  FULL: 'full',
  HALF: 'half',
} as const;

export type LegacyDashboardBlockWidthType =
  (typeof LEGACY_DASHBOARD_BLOCK_WIDTHS)[keyof typeof LEGACY_DASHBOARD_BLOCK_WIDTHS];

export interface IDashboardActivityOverviewConfig {
  hours: 24 | 48 | 168;
  interval: 'hour' | 'day';
}

export const DASHBOARD_WORK_ITEM_COLUMNS = {
  RESPONSIBLE: 'responsible',
  RESOLVED_BY: 'resolved-by',
  CLOSED_BY: 'closed-by',
  RELEASE: 'release',
  TARGET_DATE: 'target-date',
  UPDATED_AT: 'updated-at',
  PRIORITY: 'priority',
} as const;

export type DashboardWorkItemColumn =
  (typeof DASHBOARD_WORK_ITEM_COLUMNS)[keyof typeof DASHBOARD_WORK_ITEM_COLUMNS];

/** What a dashboard block shows unless configured. Unchanged — a dashboard is not a work queue. */
export const DEFAULT_DASHBOARD_WORK_ITEM_COLUMNS: DashboardWorkItemColumn[] = [
  DASHBOARD_WORK_ITEM_COLUMNS.RESPONSIBLE,
  DASHBOARD_WORK_ITEM_COLUMNS.TARGET_DATE,
  DASHBOARD_WORK_ITEM_COLUMNS.UPDATED_AT,
  DASHBOARD_WORK_ITEM_COLUMNS.PRIORITY,
];

/**
 * What the work-items list shows unless the reader says otherwise.
 *
 * Deliberately not the dashboard's default. Here the release an item is planned for earns its
 * place — it is the question a work queue is read to answer — while the target date does not: it
 * is derived from that release for most items, so it repeats the column beside it.
 */
export const DEFAULT_WORK_ITEM_LIST_COLUMNS: DashboardWorkItemColumn[] = [
  DASHBOARD_WORK_ITEM_COLUMNS.RESPONSIBLE,
  DASHBOARD_WORK_ITEM_COLUMNS.RELEASE,
  DASHBOARD_WORK_ITEM_COLUMNS.UPDATED_AT,
  DASHBOARD_WORK_ITEM_COLUMNS.PRIORITY,
];

/**
 * Every column a reader can turn on, in the order the picker offers them.
 *
 * Resolved by and Closed by sit beside Responsible, because they answer the same kind of question
 * about a different moment — and are off by default, since they are read to audit closures (I-0581).
 */
export const ALL_DASHBOARD_WORK_ITEM_COLUMNS: DashboardWorkItemColumn[] = [
  DASHBOARD_WORK_ITEM_COLUMNS.RESPONSIBLE,
  DASHBOARD_WORK_ITEM_COLUMNS.RESOLVED_BY,
  DASHBOARD_WORK_ITEM_COLUMNS.CLOSED_BY,
  DASHBOARD_WORK_ITEM_COLUMNS.RELEASE,
  DASHBOARD_WORK_ITEM_COLUMNS.TARGET_DATE,
  DASHBOARD_WORK_ITEM_COLUMNS.UPDATED_AT,
  DASHBOARD_WORK_ITEM_COLUMNS.PRIORITY,
];

export const DASHBOARD_WORK_ITEM_COLUMN_LABELS: Record<DashboardWorkItemColumn, string> = {
  responsible: 'Responsible',
  'resolved-by': 'Resolved by',
  'closed-by': 'Closed by',
  release: 'Release',
  'target-date': 'Target date',
  'updated-at': 'Updated',
  priority: 'Priority',
};

export interface IDashboardWorkItemListConfig {
  /**
   * F-0793: readers may move the border between the list and the details; each keeps their own width
   * (`@featit/shared/page-column-widths`). Absent is off. An item list has the border only with
   * `detail: 'pane'`, a next action only with `scope: 'readable'`.
   */
  readerResize?: boolean;
  stateGroup: 'open' | 'started' | 'completed' | 'all';
  itemTypeKey?: string | null;
  responsibleKey?: string | null;
  limit: 5 | 10 | 20;
  columns?: DashboardWorkItemColumn[];
  /**
   * An item-scoped page of the same work area that each item opens on, at
   * `pages/:pageId/items/:key`, instead of the item page (F-0621 AGM-42); null opens the item page.
   */
  openInPageId?: string | null;
  /**
   * `'page'`: only the children of the item an item-scoped page is opened for (AGM-42), read as the
   * reader; absent, the whole work area's items.
   */
  parent?: 'page';
  /**
   * PX-3: each item is a project with its one next step, said as a verb and opening that exact
   * thing, instead of a line of fields; with nothing waiting for the reader, who it waits on and
   * the date in `readyField` (`<templateKey>.<fieldKey>`) it is ready by. Absent, the list's lines.
   */
  nextStep?: { readyField?: string | null } | null;
  /**
   * PX-43: `'pane'` opens a selected item beside the list, as the Work items view does (its rows,
   * keys, resizable pane and close; full screen with Back on a phone), showing only what the reader
   * may read of it. Absent, each row opens the item's page.
   */
  detail?: 'pane';
  /**
   * F-0778: one list grouped by named condition trees, an item in the first group it meets
   * (`docs/concept-generic-view-engine.md` § custom groups). A package may name each group in
   * several languages; the reader's is drawn. Absent, the list is not grouped.
   */
  customGroups?: IDashboardCustomGroupsConfig | null;
}

/** F-0778: a list block's custom groups: the view engine's own grouping, with package labels. */
export interface IDashboardCustomGroupsConfig extends ICustomGroupingTextSpec {
  /** The groups drawn closed at first, by id. */
  collapsed?: string[];
}

export interface IDashboardSavedViewConfig {
  savedViewId?: string | null;
  limit: 5 | 10 | 20;
  columns?: DashboardWorkItemColumn[];
  /**
   * AGM-47: under the view, Waiting on you — the view's items where a confirmation or a question
   * waits on the reader, each with what waits, derived as Next action is. Off unless set.
   */
  waitingOnYou?: boolean;
  /**
   * PX-9: the date fields (`<templateKey>.<fieldKey>`) whose dates are promises to the customer, read
   * beside an item's service level and target date: Waiting on you takes the oldest promise first.
   */
  promiseFields?: string[];
  /**
   * PX-9: the field (`<templateKey>.<fieldKey>`) a check sets on an item it flagged (PX-16); a
   * flagged item leads Waiting on you. Absent, nothing is flagged.
   */
  flagField?: string | null;
  /**
   * AGM-50: above the view, one line of its counts by state — the totals its own read answers, in
   * the order it draws its groups. Off unless set.
   */
  measures?: boolean;
  /**
   * AGM-54: in the line of counts, Due today and Overdue — the model's `dueToday` and `overdue`
   * measures over the view's own items, for the reader's today. Drawn only with `measures`.
   */
  dueCounts?: boolean;
}

export interface IDashboardDocumentListConfig {
  /** Retired with the document tree (ADR-098): read from older pages, never written or honoured. */
  parentId?: string | null;
  limit: 5 | 10 | 20;
  /**
   * `page`: the documents on the page the block sits on, in the page's order, with the page's own
   * add/attach actions — what every page gets by default (F-0187). `work-area` (or absent, as older
   * blocks are): the work area's documents, most recently updated first.
   */
  source?: 'page' | 'work-area';
}

/** The two blocks every page starts with: its documents, and its readme (F-0187). */
export function defaultPageDocumentBlocks(newId: () => string): IDashboardBlock[] {
  return [
    {
      id: newId(),
      type: DASHBOARD_BLOCK_TYPES.DOCUMENT_LIST,
      span: DASHBOARD_BLOCK_SPANS.HALF,
      newRow: false,
      title: 'Documents',
      // Only the source: every other setting inherits the work area's block defaults (F-0195).
      config: { source: 'page' } as IDashboardDocumentListConfig,
    },
    {
      id: newId(),
      type: DASHBOARD_BLOCK_TYPES.PAGE_README,
      span: DASHBOARD_BLOCK_SPANS.HALF,
      newRow: false,
      title: 'README',
      config: {},
    },
  ];
}

/** A page's readme needs no configuration: the page and the work area's default names decide. */
export type IDashboardPageReadmeConfig = Record<never, never>;

export interface IDashboardDocumentPreviewConfig {
  documentId?: string | null;
}

export interface IDashboardRoadmapConfig {
  filter: 'future' | 'next' | 'recent';
  limit: 5 | 10 | 20;
}

/**
 * The decisions block always shows every decision waiting for the reader in its scope (F-0222) —
 * the work area it sits in, or every work area under All work areas — the same set the Decisions
 * badge counts, unshortened, so the two cannot disagree (AC-4). Its one setting is whether the
 * reader's postponed decisions are drawn under them (F-0685): absent means hidden, and they were
 * never in that set either way.
 */
export interface IDashboardDecisionsConfig {
  showPostponed?: boolean;
}

/**
 * The tasks block needs no configuration either (I-0900, mirrors Decisions): it always shows
 * everything but decisions waiting for the reader in its scope, unshortened, the same set the Tasks
 * badge counts.
 */
export interface IDashboardTasksConfig {
  /** F-0793: readers may move the border between the list and the details, as on an item list. */
  readerResize?: boolean;
}

/**
 * The item an Action bar acts on: by its key in the page's work area, or, with `itemSource: 'page'`
 * on a page scoped to an item type, the item the page is opened for (AGM-42); null until chosen.
 */
export interface IDashboardActionBarConfig {
  itemKey: string | null;
  itemSource?: 'page';
}

/** The item a Review canvas shows, by its key in the page's work area; null until it is chosen. */
export interface IDashboardReviewCanvasConfig {
  itemKey: string | null;
}

/**
 * The item an Approval panel approves, and the role its confirmation is written in: a key such as
 * `approver`, null for the default. Until a flow node names its confirming role (§20.4), the block
 * names it.
 */
export interface IDashboardApprovalPanelConfig {
  itemKey: string | null;
  confirmRole?: string | null;
  /**
   * F-0621: with the page's item bound (`itemSource: 'page'`), the panel lists that item's children
   * of this type that wait for the reader's approval, each approved in place, in place of the item's
   * own round. Absent or null, it approves the item itself.
   */
  childItemTypeKey?: string | null;
  /**
   * PX-6: the setting each listed child is shown in (a feed post, a story, a web page), named by the
   * package from one of the child's fields. Absent, every child is shown on its own.
   */
  settings?: IDesignSettingsConfig | null;
  /**
   * PX-13: where the plan put to a customer is read (ScopeEstimateCard), each part a
   * `<template>.<field>` path of the fields its agreement names, and the ISO 4217 code the money is
   * in. Absent, the panel draws no plan.
   */
  plan?: IDashboardPlanFields | null;
}

/** PX-6: the settings a design can be shown in, as a customer would meet it. */
export const DESIGN_SETTINGS = ['feed', 'story', 'webPage'] as const;
export type DesignSettingType = (typeof DESIGN_SETTINGS)[number];

/**
 * PX-6: which field of a child names its setting (`<template>.<field>`, such as
 * `agencyDeliverableFields.formatFamily`), and the setting each of its values is shown in. A value
 * the map does not name is shown on its own.
 */
export interface IDesignSettingsConfig {
  field: string;
  frames: Record<string, DesignSettingType>;
}

/** PX-13: the fields a plan's parts are read from, and its currency (`EUR`). */
export interface IDashboardPlanFields {
  scope?: string | null;
  price?: string | null;
  priceUpTo?: string | null;
  changePrice?: string | null;
  date?: string | null;
  currency: string;
}

/**
 * The item an Item fields block shows and the fields it shows of it, each `<templateKey>.<fieldKey>`
 * as a field set names them (AGM-44). The reader still sees only what their read returns.
 */
export interface IDashboardItemFieldsConfig {
  itemKey: string | null;
  fields: string[];
  /**
   * F-0741 PX-100: also a line naming who asked for the item, the reporter of the item it
   * `implements` (a Job's Request), else of its parent, read as the reader. A member's read only.
   */
  requester?: boolean;
}

/** The item a Next action block reads the reader's next step on, by its key; null until chosen. */
export interface IDashboardNextActionConfig {
  /**
   * F-0793: readers may move the border between the list and the details; each keeps their own width
   * (`@featit/shared/page-column-widths`). Absent is off. An item list has the border only with
   * `detail: 'pane'`, a next action only with `scope: 'readable'`.
   */
  readerResize?: boolean;
  itemKey: string | null;
  /**
   * PX-44: `'readable'` lists what waits for the reader across every item they may read in the work
   * area (a guest's approvals, plans and questions, each naming its item and opening where she acts)
   * in place of one item's step. Absent, the block reads the one item.
   */
  scope?: NextActionScopeType;
  /** PX-44: the field (`<templateKey>.<fieldKey>`) an entry's due date is read from, as the reader may read it. */
  dueField?: string | null;
}

/** PX-44: the one wider scope a Next action block reads; absent is the one item. */
export type NextActionScopeType = 'readable';

/** The item a File gallery shows the files of, with its children's, by key; null until chosen. */
export interface IDashboardFileGalleryConfig {
  itemKey: string | null;
  /** AGM-49, by a field: a single-select field of the children (`<templateKey>.<fieldKey>`). */
  groupBy?: string | null;
  /** AGM-49: keep only the children whose `filterField` holds one of `filterValues` (option ids). */
  filterField?: string | null;
  filterValues?: string[] | null;
}

/**
 * The item a Coverage grid counts the children of, by key; null until chosen. With `rows` and
 * `columns` (both or neither), each a single-select field named `<templateKey>.<fieldKey>`, the
 * children are drawn in a grid of those two fields instead of by state (AGM-53).
 */
export interface IDashboardCoverageGridConfig {
  itemKey: string | null;
  rows?: string | null;
  columns?: string | null;
}

/**
 * The item a Trail block shows the trail of, by key; null until chosen (AGM-55). `contactField`, a
 * field named `<templateKey>.<fieldKey>`, is the contact a guest reads on each step in place of who
 * did it, when their field set discloses it (D-0379); without it, or empty, a guest reads "The team".
 */
export interface IDashboardTrailConfig {
  itemKey: string | null;
  contactField?: string | null;
}

/**
 * PX-5: the project a Project progress block shows, by key or as the page's item; the type of its
 * children that are its designs; and the fields (`<templateKey>.<fieldKey>`) its ready-by date and
 * the person looking after it are read from, each read as the reader may read it.
 */
export interface IDashboardProjectProgressConfig {
  itemKey: string | null;
  childItemTypeKey?: string | null;
  readyField?: string | null;
  contactField?: string | null;
}

/**
 * PX-16: the item a Design grid lays out the designs of, by key or as the page's item; the two
 * single-select fields (`<templateKey>.<fieldKey>`) its rows and columns are, both required; and the
 * text field a check writes its sentence into when it flags a design (`flagField`), if any.
 */
export interface IDashboardDesignGridConfig {
  itemKey: string | null;
  rows?: string | null;
  columns?: string | null;
  flagField?: string | null;
}

/** The item a Questions block shows the reader's questions on, by key; null until chosen (AGM-58). */
export interface IDashboardItemQuestionsConfig {
  itemKey: string | null;
  /**
   * PX-5: the block leads the page, right after its step, while a question waits for the reader
   * (`pageBlocksInReadingOrder` in `item-page-blocks.ts`); absent, it keeps its place.
   */
  leadsWhile?: 'questionsWait';
}

/** The item type a New item form files, by its key; null until chosen (AGM-61). */
export interface IDashboardNewItemConfig {
  itemTypeKey: string | null;
  /**
   * The words the form adds, per place and language (`new-item-block-texts.ts`); absent, it draws
   * only its labels.
   */
  texts?: NewItemBlockTextsType;
  /**
   * PX-2: who has a sent request and when the sender will hear back (`reply-promise.ts`); absent,
   * the form says only that it was received.
   */
  replyPromise?: IReplyPromise;
  /** PX-36 (I-2384): one-tap chips under the message box (`newItemBlockChipsOf`); absent, none. */
  chips?: INewItemBlockChip[];
}

export type DashboardBlockConfig =
  | IDashboardActivityOverviewConfig
  | IDashboardWorkItemListConfig
  | IDashboardSavedViewConfig
  | IDashboardRoadmapConfig
  | IDashboardDocumentListConfig
  | IDashboardDocumentPreviewConfig
  | IDashboardPageReadmeConfig
  | IDashboardDecisionsConfig
  | IDashboardTasksConfig
  | IDashboardActionBarConfig
  | IDashboardReviewCanvasConfig
  | IDashboardApprovalPanelConfig
  | IDashboardItemFieldsConfig
  | IDashboardNextActionConfig
  | IDashboardFileGalleryConfig
  | IDashboardCoverageGridConfig
  | IDashboardTrailConfig
  | IDashboardItemQuestionsConfig
  | IDashboardNewItemConfig
  | IDashboardProjectProgressConfig
  | IDashboardDesignGridConfig;

export interface IDashboardBlock {
  id: string;
  type: DashboardBlockType;
  /**
   * How wide the block is, as a fraction of a 12-unit row (F-0203). One of
   * `DASHBOARD_BLOCK_SPANS`; the rows a page falls into are computed from these by
   * `packBlocksIntoRows`, never stored.
   */
  span: DashboardBlockSpanType;
  /** Starts a new row even when the row being built still has room for this block. */
  newRow: boolean;
  /**
   * The height this block gives its row (F-0686). Absent is Auto — fit the content — which is every
   * block stored before it; a row is as tall as the tallest height its blocks carry.
   */
  height?: DashboardRowHeightType;
  title?: string | null;
  config: DashboardBlockConfig;
}

/** The users and roles a page is shared with. */
export interface IPageShares {
  userIds: string[];
  roleKeys: string[];
}

export interface IWorkAreaDashboardPage {
  id: string;
  projectId: string;
  title: string;
  isHome: boolean;
  /**
   * The page this one lives under, or null at the top level (F-0201). Home is always null: it is
   * the work area's root, not a page anything nests under.
   */
  parentPageId: string | null;
  /**
   * The work area's order for its pages within their parent, shared by every member; Home is
   * always first.
   */
  position: number;
  /** A Lucide name from `PAGE_ICON_NAMES`, or null for `DEFAULT_PAGE_ICON`. */
  icon: string | null;
  /**
   * The permission that shows this page (T-0783, D-0350); null shows it to everyone who may read the
   * work area. Presentation, never a grant: a block on the page still reads as the reader.
   */
  audiencePermission?: string | null;
  /**
   * The item type this page is opened for, one item at a time, at `pages/:pageId/items/:key`
   * (F-0621 AGM-42); null (or absent from an older server) is a page of the whole work area.
   */
  scopeItemTypeKey?: string | null;
  /**
   * Who the page is shared with, for a reader who manages the pages (and absent for everyone else).
   * A share is a permission on this page only, caused by the share.
   */
  sharedWith?: IPageShares;
  blocks: IDashboardBlock[];
  revision: number;
  /** Both null once the identity is deleted — the page survives its author (ADR-045). */
  createdById: string | null;
  updatedById: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface IWorkAreaDashboardPageCheckpoint {
  count: number;
  revision: number;
  updatedAt: string;
  /** The effective block defaults, hashed: a work-area default changes no page row (F-0195). */
  digest?: string;
}

export interface IPageSharingOptions {
  /**
   * The work area's roles, each with the permissions it grants: what the editor checks a block
   * against for the page's audience (AGM-17). Absent from an older server.
   */
  roles: Array<{ key: string; name: string; permissions?: string[] }>;
  /**
   * The work area's members. `roleNames` are the roles each holds here, by name, so "Share with"
   * can sort people by role and say it under each name (I-2234); absent from an older server.
   */
  users: Array<{ id: string; name: string; roleNames?: string[] }>;
  /** The permissions a page may name as its audience. */
  permissions: Array<{ key: string; description: string }>;
}

export interface IWorkAreaDashboardPageSnapshot {
  items: IWorkAreaDashboardPage[];
  canManage: boolean;
  checkpoint: IWorkAreaDashboardPageCheckpoint;
  /** Every block setting's default in force for this work area (F-0195). */
  blockDefaults?: import('../block-defaults.js').EffectiveBlockDefaultsType;
  /**
   * What a manager may share a page with and require for it (T-0783, D-0350); sent only to a reader
   * who manages the pages.
   */
  sharing?: IPageSharingOptions;
}

export interface IWorkAreaOverviewBlock extends IDashboardBlock {
  /** Empty means every work area currently accessible to the user. */
  workAreaIds: string[];
}

export interface IWorkAreaOverviewSavedView {
  id: string;
  name: string;
  /** Empty means every work area currently accessible to the user. */
  workAreaIds: string[];
  layout: ViewLayoutType;
  filters: Record<string, unknown>;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  createdAt: string;
  updatedAt: string;
}

export interface IWorkAreaOverviewPreferences {
  revision: number;
  blocks: IWorkAreaOverviewBlock[];
  views: IWorkAreaOverviewSavedView[];
  updatedAt: string | null;
}

export interface IWorkAreaNavigationPreferences {
  revision: number;
  workAreaOrder: string[];
  /**
   * F-0380: the reader's own arrangement of the namespaces in the work-area selector — the same
   * revision as `workAreaOrder`, because both are "how the selector is arranged" and a reload needs
   * them together. An id for a namespace the reader no longer belongs to, or never did, is simply
   * never matched when the order is read back (`orderedWorkAreasAcrossNamespaces` in
   * `work-area-hierarchy.ts`) — the same way an unknown `workAreaOrder` id is ignored rather than
   * refused.
   */
  namespaceOrder: string[];
  updatedAt: string | null;
}

/**
 * F-0343: which destinations fill the phone's main toolbar, and in what order. The same shape the
 * iOS shell's `tabs` bridge message carries, after `toolbarOrderOf` validates it — see
 * `shell-bridge.ts`.
 */
export interface IToolbarPreferences {
  revision: number;
  /**
   * F-0562 (AC-17.6a): toolbar keys — a fixed tab id, a menu destination or a place. An order stored
   * before F-0562 holds fixed ids only and reads back unchanged.
   */
  order: ToolbarKeyType[];
  /**
   * F-0562: the descriptor of every place `order` names (a view, a page, Parts, a work area's
   * Settings). Absent or empty when the order names none.
   */
  places?: IToolbarPlace[];
  /**
   * F-0414 (AC-18.2): the view last picked from the More menu, shown in the bar's last slot before
   * More. `null` or absent: the slot shows `order`'s own destination there.
   */
  recent?: IShellRecentView | null;
  /** F-0414: the person keeps that slot fixed on its configured destination. Off by default. */
  recentPinned?: boolean;
  updatedAt: string | null;
}

/**
 * IU-12 (`docs/concept-iphone-ux.md#iu-12-activity-roadmap-and-agents-in-the-phone-form`, F-0507):
 * the newest Activity row this person had already read, the last time they left a work area's
 * Activity feed. One per work area — each work area's feed is its own timeline, so "new since your
 * last visit" means new in THIS one, never across every work area at once.
 *
 * Last write wins, with no revision check (the same reasoning as `IAgentUsageChartPreferences`): it
 * is a bookmark the reader's own next visit immediately overwrites, never a value two sessions
 * could meaningfully disagree about.
 */
export interface IActivityLastVisitPreferences {
  /** `null`: never visited this work area's Activity feed — every loaded row reads as new. */
  visitedAt: string | null;
}

/**
 * I-2233 (`docs/concept-invitations-to-several-work-areas.md` AC-PI.5): the pending invitations whose
 * notice this person has already been shown. Kept with the person, not the device, so the notice
 * that an invitation is waiting appears once per invitation, never again on the next page load or
 * on their other devices. Newest last, capped at `INVITATIONS_SEEN_LIMIT`.
 */
export interface IInvitationsSeenPreferences {
  seen: string[];
}

/** I-2233: how many seen invitation ids are kept; the oldest go first. */
export const INVITATIONS_SEEN_LIMIT = 100;

/** IU-4 (`docs/concept-iphone-ux.md#iu-4-phone-type-and-spacing`): Comfortable, the iOS default
 * Dynamic Type size; Compact restores the denser table-like sizes. */
export const PHONE_DENSITIES = {
  COMFORTABLE: 'comfortable',
  COMPACT: 'compact',
} as const;
export type PhoneDensityType = (typeof PHONE_DENSITIES)[keyof typeof PHONE_DENSITIES];

/** *Settings*: "List titles" — up to two lines, or cut to one. */
export const PHONE_LIST_TITLE_LINES = {
  TWO: 'two',
  ONE: 'one',
} as const;
export type PhoneListTitleLinesType =
  (typeof PHONE_LIST_TITLE_LINES)[keyof typeof PHONE_LIST_TITLE_LINES];

/** *Settings*: the leading swipe on Items — the list's one forward action, or none. */
export const PHONE_LEADING_SWIPE_ITEMS = {
  FORWARD_STATE: 'forward-state',
  ASSIGN_TO_ME: 'assign-to-me',
  NONE: 'none',
} as const;
export type PhoneLeadingSwipeItemsType =
  (typeof PHONE_LEADING_SWIPE_ITEMS)[keyof typeof PHONE_LEADING_SWIPE_ITEMS];

/** *Settings*: "Roadmap shows" — cards per release, or the vertical timeline. */
export const PHONE_ROADMAP_SHOWS = {
  RELEASES: 'releases',
  TIMELINE: 'timeline',
} as const;
export type PhoneRoadmapShowsType = (typeof PHONE_ROADMAP_SHOWS)[keyof typeof PHONE_ROADMAP_SHOWS];

/**
 * *Settings*: "After answering" a decision. F-0567 (NX-4): *Show what's next* — the answered
 * decision with its next-step bar — replaced *Stay*; a stored `stay` reads as it
 * (`phoneAfterAnsweringOf`).
 */
export const PHONE_AFTER_ANSWERING = {
  NEXT_WAITING: 'next-waiting',
  BACK_TO_LIST: 'back-to-list',
  SHOW_NEXT: 'show-next',
} as const;
export type PhoneAfterAnsweringType =
  (typeof PHONE_AFTER_ANSWERING)[keyof typeof PHONE_AFTER_ANSWERING];

/** F-0567: the value *Show what's next* replaced, still stored for some and sent by older clients. */
export const PHONE_AFTER_ANSWERING_LEGACY_STAY = 'stay';

/**
 * F-0567 (NX-4): the one mapping from a stored or sent *After answering* to a current value — a
 * legacy `stay` is `show-next`, so nobody's choice is lost; anything else unknown is `null`.
 */
export function phoneAfterAnsweringOf(value: unknown): PhoneAfterAnsweringType | null {
  if (value === PHONE_AFTER_ANSWERING_LEGACY_STAY) return PHONE_AFTER_ANSWERING.SHOW_NEXT;
  const values: readonly unknown[] = Object.values(PHONE_AFTER_ANSWERING);
  return values.includes(value) ? (value as PhoneAfterAnsweringType) : null;
}

/** *Settings*: the leading swipe on Decisions — Postpone, the only forward action, or none. */
export const PHONE_LEADING_SWIPE_DECISIONS = {
  POSTPONE: 'postpone',
  NONE: 'none',
} as const;
export type PhoneLeadingSwipeDecisionsType =
  (typeof PHONE_LEADING_SWIPE_DECISIONS)[keyof typeof PHONE_LEADING_SWIPE_DECISIONS];

/** *Settings*: "Search field" — hidden until the list is pulled down, or always shown. */
export const PHONE_SEARCH_FIELD = {
  HIDDEN_UNTIL_PULL: 'hidden-until-pull',
  ALWAYS_SHOWN: 'always-shown',
} as const;
export type PhoneSearchFieldType = (typeof PHONE_SEARCH_FIELD)[keyof typeof PHONE_SEARCH_FIELD];

/**
 * F-0530 (AC-19.8, the captain's ruling for Jay): "Badge counts" — the Decisions badge counts the
 * app's work area (the default), or every work area.
 */
export const PHONE_BADGE_COUNTS = {
  WORK_AREA: 'work-area',
  ALL_WORK_AREAS: 'all-work-areas',
} as const;
export type PhoneBadgeCountsType = (typeof PHONE_BADGE_COUNTS)[keyof typeof PHONE_BADGE_COUNTS];

/**
 * F-0532 (AC-19.18): "Work area switcher order" — the order of the work-area sheet: Recent, then the
 * custom order (the default, AC-19.3); the custom order alone; or alphabetical by the row's label.
 */
export const PHONE_SWITCHER_ORDER = {
  RECENT_THEN_CUSTOM: 'recent-then-custom',
  CUSTOM: 'custom',
  ALPHABETICAL: 'alphabetical',
} as const;
export type PhoneSwitcherOrderType =
  (typeof PHONE_SWITCHER_ORDER)[keyof typeof PHONE_SWITCHER_ORDER];

/**
 * IU-4 (`docs/concept-iphone-ux.md#iu-4-phone-type-and-spacing`, *Settings*): every phone setting
 * the concept lists, stored and validated together — including the ones whose UI lands in a later
 * slice (swipes, the tab bar, after answering, the two confirmations, the search field) — the same
 * "omitted key keeps its stored value" shape as `IToolbarPreferences`, in the same `preferences`
 * column.
 */
export interface IPhonePreferences {
  revision: number;
  density: PhoneDensityType;
  listTitleLines: PhoneListTitleLinesType;
  swipeActions: boolean;
  leadingSwipeItems: PhoneLeadingSwipeItemsType;
  roadmapShows: PhoneRoadmapShowsType;
  /** iOS 26's tab bar shrinking on scroll; Off keeps it fixed, as on iOS 17/18. */
  tabBarShrinksOnScroll: boolean;
  afterAnswering: PhoneAfterAnsweringType;
  confirmBeforeAnswering: boolean;
  confirmBeforeWithdrawingOrDeclining: boolean;
  leadingSwipeDecisions: PhoneLeadingSwipeDecisionsType;
  searchField: PhoneSearchFieldType;
  badgeCounts: PhoneBadgeCountsType;
  switcherOrder: PhoneSwitcherOrderType;
  updatedAt: string | null;
  /**
   * I-1295 (IU-4 *Settings*): the settings the person chose. Only these are stored; every other
   * one is the current default, so a default can tell itself apart from the same value chosen (the
   * desktop split's *After answering*). Absent from a server older than this field.
   */
  chosen?: PhonePreferenceKeyType[];
}

/** I-1295: one phone setting, as `IPhonePreferences.chosen` names it. */
export type PhonePreferenceKeyType = Exclude<
  keyof IPhonePreferences,
  'revision' | 'updatedAt' | 'chosen'
>;

/**
 * I-1577: what a phone settings save sends — the revision it read, and only the settings it
 * changes. `null` forgets a choice, so the setting's default applies again.
 */
export type PhonePreferencesSaveType = { expectedRevision: number } & {
  [K in PhonePreferenceKeyType]?: IPhonePreferences[K] | null;
};

export const ISSUE_STATUSES = {
  OPEN: 'open',
  IN_PROGRESS: 'in_progress',
  IN_REVIEW: 'in_review',
  RESOLVED: 'resolved',
  CLOSED: 'closed',
} as const;

export type IssueStatusType = (typeof ISSUE_STATUSES)[keyof typeof ISSUE_STATUSES];

export const ISSUE_SEVERITIES = {
  CRITICAL: 'critical',
  HIGH: 'high',
  MEDIUM: 'medium',
  LOW: 'low',
} as const;

export type IssueSeverityType = (typeof ISSUE_SEVERITIES)[keyof typeof ISSUE_SEVERITIES];

export interface IIssue {
  id: string;
  projectId: string;
  title: string;
  description: string;
  status: IssueStatusType;
  severity: IssueSeverityType;
  reporterId: string;
  assigneeId?: string;
  labels: string[];
  createdAt: string;
  updatedAt: string;
}

export const FEATURE_STATUSES = {
  PROPOSED: 'proposed',
  PLANNED: 'planned',
  IN_PROGRESS: 'in_progress',
  COMPLETED: 'completed',
  REJECTED: 'rejected',
} as const;

export type FeatureStatusType = (typeof FEATURE_STATUSES)[keyof typeof FEATURE_STATUSES];

export interface IFeature {
  id: string;
  projectId: string;
  title: string;
  description: string;
  status: FeatureStatusType;
  requesterId: string;
  assigneeId?: string;
  priority: number;
  createdAt: string;
  updatedAt: string;
}

export interface IFeedback {
  id: string;
  projectId: string;
  title: string;
  content: string;
  authorId: string;
  category: FeedbackCategoryType;
  environment?: IEnvironmentInfo;
  assigneeId?: string;
  workflowStateId?: string;
  position?: number;
  images?: IFeedbackImage[];
  aiSuggestion?: IAiSuggestion;
  createdAt: string;
  updatedAt: string;
  /** Every reference this entry's own `content` cites, resolved (I-1061, extending I-0537). */
  contentReferences?: IResolvedReference[];
}

export interface IEnvironmentInfo {
  browser?: string;
  os?: string;
  device?: string;
  screenResolution?: string;
  appVersion?: string;
  url?: string;
}

export interface IFeedbackImage {
  id: string;
  feedbackId: string;
  workItemId?: string;
  filename: string;
  url?: string;
  thumbnailUrl?: string;
  mimeType: string;
  size: number;
  width?: number;
  height?: number;
  annotations: IAnnotation[];
  sortOrder: number;
  authorId?: string | null;
  author?: IUser | null;
  createdAt: string;
  updatedAt: string;
}

export interface IAnnotation {
  id: string;
  type: AnnotationToolType;
  points: number[][];
  color: string;
  strokeWidth: number;
  text?: string;
  number?: number;
  fillColor?: string;
  fontSize?: number;
}

export const ANNOTATION_TOOLS = {
  ARROW: 'arrow',
  RECTANGLE: 'rectangle',
  CIRCLE: 'circle',
  FREEHAND: 'freehand',
  TEXT: 'text',
  CALLOUT: 'callout',
  BLUR: 'blur',
} as const;

export type AnnotationToolType = (typeof ANNOTATION_TOOLS)[keyof typeof ANNOTATION_TOOLS];

export const FEEDBACK_CATEGORIES = {
  GENERAL: 'general',
  UI_UX: 'ui_ux',
  PERFORMANCE: 'performance',
  FEATURE_REQUEST: 'feature_request',
  BUG_REPORT: 'bug_report',
} as const;

export type FeedbackCategoryType = (typeof FEEDBACK_CATEGORIES)[keyof typeof FEEDBACK_CATEGORIES];

export interface ICommentActorUser {
  id: string;
  email?: string | null;
  name?: string | null;
}

export interface ICommentActorAgent {
  id: string;
  ownerUserId: string;
  key: string;
  displayName: string;
  kind: string;
  status: string;
  lastSeenAt?: string | null;
  ownerUser?: ICommentActorUser | null;
}

export interface ICommentActorRef {
  /** `external`: the item's external reporter wrote it — an email reply (F-0263). */
  actorType: 'user' | 'agent' | 'external';
  userId?: string | null;
  agentId?: string | null;
  agentSessionId?: string | null;
  agentSessionRef?: string | null;
  displayNameSnapshot?: string | null;
  /** The external reporter's trust source (`email-inbox`) and their id there, when `external`. */
  externalSourceKey?: string | null;
  externalSubjectId?: string | null;
  user?: ICommentActorUser | null;
  agent?: ICommentActorAgent | null;
  /** The agent now holding this author's work, when the author was merged away (I-0314). */
  mergedIntoAgent?: IAgentMergeTarget | null;
}

export interface IComment {
  id: string;
  entityType: CommentEntityType;
  entityId: string;
  /** F-0621 AGM-15: the place in one file version it points at; absent or null for none. */
  anchor?: import('../comment-anchor.js').ICommentAnchor | null;
  /** F-0638 AC-CV.90: where it came from, when a person saved it from a conversation; else absent. */
  provenance?: import('../comment-provenance.js').ICommentProvenance | null;
  /** Null when no account wrote it: an external reporter's email reply (F-0263). */
  authorId: string | null;
  content: string;
  parentId?: string;
  mentions?: string[];
  editedAt?: string;
  author?: IUser;
  actor?: ICommentActorRef | null;
  /** An AI wrote it: an agent, or one that could not be named (I-0644, AL-7). */
  aiWritten?: boolean;
  /**
   * F-0266 RL-1a: `reporters` when it is addressed to the item's reporters; `author` (UXA-10) when
   * it is a guest's pin or an answer to one, for the team and the pin's author only; else internal.
   */
  visibility?: 'internal' | 'reporters' | 'author';
  /**
   * PX-16: set on a comment an agent wrote for a customer that no person has sent: who it will reach
   * once sent. Such a comment is internal until a professional sends it. Absent on every other comment.
   * `guests` (PX-106) is an agent's question to a guest role, sent through its question's own route.
   */
  draftAudience?: 'reporters' | 'author' | 'guests' | null;
  /** PX-16: what the agent says the draft is based on, as short phrases. */
  draftBasis?: string[] | null;
  /**
   * `question` for a turn that asks (F-0179). Absent from a server older than question turns, which
   * is read as `comment` — and such a turn's `content` is still a readable question (QA-13).
   */
  kind?: CommentKindType;
  /** The question a `question` turn asks, as this reader may act on it. */
  question?: IQuestionTurnView | null;
  /** Kept at the top of its item's comments (I-0264). Absent from a server older than pinning. */
  isPinned?: boolean;
  pinnedAt?: string | null;
  pinnedById?: string | null;
  /**
   * Every reference this comment's own text cites, resolved in the comment's work area (I-1061) —
   * the same resolution an item's description already carries as `WorkItem.contentReferences`
   * (I-0537). Absent when the server did not resolve it (a caller that passed no reader context,
   * or a server older than this field): a comment with no references and one whose references were
   * never asked for read the same, by design — the renderer treats a missing array as "nothing to
   * link", never as an error.
   */
  contentReferences?: IResolvedReference[];
  /**
   * T-0569 OE-2: on a write's answer only, the activity-journal revision the write left this comment
   * at — a bigint as a decimal string — which its data-change echo carries. Absent on every read.
   */
  revision?: string;
  createdAt: string;
  updatedAt: string;
}

/**
 * One page of an item's comment trail (I-0228).
 *
 * The route serves the newest `limit` comments rather than however many an item has accumulated —
 * the longest trail in the field is 191 of them, a quarter of a megabyte, and was being sent in
 * full to anyone who opened the item. `total` is what keeps that from being a silent cut: a reader
 * can be told how much is behind what they are looking at, and ask for it.
 */
export interface ICommentPage {
  /** Oldest first, whichever end of the trail the window was taken from. */
  comments: IComment[];
  /** Every comment on the item, not only the ones on this page. */
  total: number;
  /** Comments exist before the first one here. */
  hasOlder: boolean;
  /** Reads the page before this one. Null when there is nothing before it. */
  olderCursor: string | null;
  /**
   * Every pinned comment of the item, newest pin first, whether or not it is on this page (I-0264).
   * Absent from a server older than pinning.
   */
  pinned?: IComment[];
}

export const COMMENT_ENTITIES = {
  ISSUE: 'issue',
  FEATURE: 'feature',
  FEEDBACK: 'feedback',
  WORK_ITEM: 'work-item',
} as const;

export type CommentEntityType = (typeof COMMENT_ENTITIES)[keyof typeof COMMENT_ENTITIES];

// AI Suggestion types

export const AI_CLASSIFICATIONS = {
  BUG: 'bug',
  FEATURE_REQUEST: 'feature_request',
  IMPROVEMENT: 'improvement',
  UX_ISSUE: 'ux_issue',
  DATA_ISSUE: 'data_issue',
  QUESTION: 'question',
} as const;

export type AiClassificationType = (typeof AI_CLASSIFICATIONS)[keyof typeof AI_CLASSIFICATIONS];

export const AI_SEVERITIES = {
  CRITICAL: 'critical',
  MAJOR: 'major',
  MINOR: 'minor',
  COSMETIC: 'cosmetic',
} as const;

export type AiSeverityType = (typeof AI_SEVERITIES)[keyof typeof AI_SEVERITIES];

export const AI_PRIORITIES = {
  URGENT: 'urgent',
  HIGH: 'high',
  MEDIUM: 'medium',
  LOW: 'low',
} as const;

export type AiPriorityType = (typeof AI_PRIORITIES)[keyof typeof AI_PRIORITIES];

export const AI_SUGGESTION_STATUSES = {
  QUEUED: 'queued',
  PROCESSING: 'processing',
  COMPLETE: 'complete',
  FAILED: 'failed',
} as const;

export type AiSuggestionStatusType =
  (typeof AI_SUGGESTION_STATUSES)[keyof typeof AI_SUGGESTION_STATUSES];

export interface IAiSuggestion {
  id: string;
  feedbackId: string;
  status: AiSuggestionStatusType;
  classification?: AiClassificationType;
  title?: string;
  summary?: string;
  severity?: AiSeverityType;
  priority?: AiPriorityType;
  priorityReasoning?: string;
  reproductionSteps?: string[];
  expectedBehavior?: string;
  actualBehavior?: string;
  affectedComponent?: string;
  acceptanceCriteria?: string[];
  userStories?: string[];
  suggestedDataStructures?: string;
  affectedEndpoints?: string[];
  relatedItems?: string[];
  confidence?: number;
  analysisNotes?: string;
  /** Why the last analysis failed, in FEATit's words (I-0746) — never a provider's response. */
  errorMessage?: string;
  createdAt: string;
  updatedAt: string;
}

// API response types

export interface IPaginatedResponse<T> {
  data: T[];
  cursor: string | null;
  hasMore: boolean;
}

export interface IAuthResponse {
  accessToken: string;
  refreshToken: string;
  user: IUser;
}

// Workflow types

export const STATUS_GROUPS = {
  BACKLOG: 'backlog',
  UNSTARTED: 'unstarted',
  STARTED: 'started',
  COMPLETED: 'completed',
  CANCELLED: 'cancelled',
} as const;

export type StatusGroupType = (typeof STATUS_GROUPS)[keyof typeof STATUS_GROUPS];

export interface IWorkflowState {
  id: string;
  projectId: string;
  name: string;
  group: StatusGroupType;
  color: string;
  position: number;
  createdAt?: string;
  updatedAt?: string;
}

/**
 * A work area's workflow states. Its transition graph is not here: it is the policy graph every move
 * reads, stated per item type by `workAreaEffectiveRules.transitionGraphs` (ADR-131).
 */
export interface IProjectWorkflow {
  states: IWorkflowState[];
}

// Labels

export interface ILabel {
  id: string;
  projectId: string;
  name: string;
  color: string;
  createdAt?: string;
}

// Saved Views

export const VIEW_LAYOUTS = {
  LIST: 'list',
  BOARD: 'board',
  TABLE: 'table',
  MATRIX: 'matrix',
  // F-0205: a view that draws its counts as charts.
  CHART: 'chart',
} as const;

export type ViewLayoutType = (typeof VIEW_LAYOUTS)[keyof typeof VIEW_LAYOUTS];

/**
 * A row is a view, drawn by its own definition, or a folder, which only ever holds other rows, or
 * (I-1335) a built-in view's place in the tree: no definition, only `seedId`, where it sits and
 * whether the work area has hidden it.
 */
export type SavedViewKind = 'view' | 'folder' | 'builtin';

export interface ISavedView {
  id: string;
  projectId: string;
  userId: string;
  name: string;
  layout: ViewLayoutType;
  filters: Record<string, unknown>;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  isShared: boolean;
  /**
   * F-0363 slice 2: whether this row is a view or a folder; a folder's `layout`/`filters`/
   * `sortBy`/`sortOrder` mean nothing. Mirrors `backend/src/projects/entities/saved-view.entity.ts`,
   * which always sets it — optional here only so a fixture written before slice 2 still
   * typechecks; code that reads it treats an absent value as `'view'`, the column's own default.
   */
  kind?: SavedViewKind;
  /**
   * The folder this row lives under, or null at the top level (F-0363 slice 2). Optional for the
   * same reason as `kind`; an absent value reads as the top level, the column's own default.
   */
  parentFolderId?: string | null;
  /**
   * The order within `parentFolderId` — within the top level where that is null (F-0363 slice 2).
   * Optional for the same reason as `kind`; an absent value reads as `0`, the column's own default.
   */
  position?: number;
  /** I-1335: the built-in view a `builtin` row places (`roadmap`); absent or null on every other row. */
  seedId?: string | null;
  /** I-1335: a built-in view this work area has hidden from its tree. */
  hidden?: boolean;
  createdAt: string;
  updatedAt: string;
}

// Activity types

export const ACTIVITY_TYPES = {
  STATUS_CHANGED: 'status_changed',
  ASSIGNED: 'assigned',
  UNASSIGNED: 'unassigned',
  LABEL_ADDED: 'label_added',
  LABEL_REMOVED: 'label_removed',
  PRIORITY_CHANGED: 'priority_changed',
  COMMENT_ADDED: 'comment_added',
  COMMENT_PINNED: 'comment_pinned',
  COMMENT_UNPINNED: 'comment_unpinned',
  EDITED: 'edited',
  CREATED: 'created',
} as const;

export type ActivityType = (typeof ACTIVITY_TYPES)[keyof typeof ACTIVITY_TYPES];

export interface IActivity {
  id: string;
  entityType: CommentEntityType;
  entityId: string;
  actorId: string;
  type: ActivityType;
  data: Record<string, unknown>;
  createdAt: string;
}

// Notification types

/**
 * The things worth telling somebody about: four that happen on an item, and one about the reader's
 * own credentials. These are the values the producers actually write — the previous set named events
 * nothing emitted.
 */
export const NOTIFICATION_TYPES = {
  RESPONSIBLE_CHANGED: 'responsible_changed',
  MENTIONED: 'mentioned',
  STATE_CHANGED: 'state_changed',
  COMMENT_ADDED: 'comment_added',
  /**
   * One of the reader's credentials was revoked because a refresh secret it had already rotated past
   * was presented again — a copy exists somewhere (I-0394). About a credential, not an item.
   */
  CREDENTIAL_REVOKED_AFTER_REUSE: 'credential_revoked_after_reuse',
  /**
   * Somebody asked to reset the reader's password more often than the limit allows, so further asks
   * were refused for the rest of the window (I-2072). Told once per window, in the app only: a mail
   * would count against the same limit. About the reader's account, not an item.
   */
  PASSWORD_RESET_LIMITED: 'password_reset_limited',
  /**
   * The reader's personal handle, which they never chose, was replaced by the namespace's key
   * (D-0077, migration `1786890000003`). About their namespace, not an item; it opens the profile,
   * where they can choose a handle.
   */
  NAMESPACE_HANDLE_REPLACED: 'namespace_handle_replaced',
  /**
   * An agent the reader owns moved an item into progress under a WIP-cap override that has lapsed —
   * expired, or recorded but unusable — so the work area's own cap applied instead (I-0701). Told
   * once per lapse, to the owner, who is the only one who can renew or remove it. Opens that item.
   */
  WIP_OVERRIDE_LAPSED: 'wip_override_lapsed',
  /**
   * An agent's move is held for a person, and the reader is one it waits on: the agent's owner or the
   * item's responsible (F-0275 GV-2e). Critical while the request is pending. Opens the item.
   */
  MOVE_REQUESTED: 'move_requested',
  /** A person declined the reading agent's held move, with a note (F-0275 GV-2e). Opens the item. */
  MOVE_DECLINED: 'move_declined',
  /**
   * A workflow flow's `notify` action told the reader what happened to an item they are responsible
   * for, reported or asked about — in the entered node's `reporterLabel` (F-0300 WN-13). Opens it.
   */
  FLOW_NOTICE: 'flow_notice',
  /**
   * The reader's namespace went over a plan limit and its grace month started (F-0302, concept-billing
   * §8.4). Told once per grace month to each owner. About the namespace, not an item; it opens Plan &
   * billing.
   */
  PLAN_LIMIT_WARNING: 'plan_limit_warning',
  /**
   * The owner flow told the reader something waits or is late on an idea, or has nobody to move it:
   * nobody is named to write its proposal, the proposal is late, the idea's owner is late with an
   * answer (F-0461), or a proposal waits and no owner is named (F-0466, OF-84). Also an overrule
   * told to the technical lead, or to the managers when none is named (F-0467, OF-101). Told once,
   * never to the late one themselves. Opens the idea.
   */
  OWNER_FLOW_NOTICE: 'owner_flow_notice',
  /**
   * Somebody asked for an agent to be created, and the reader holds `agent.approve` (I-1067,
   * `concept-access-control.md` §21). Critical while the request waits; settles to info once anyone
   * answers it. Not about an item; it opens Settings → Agent requests.
   */
  AGENT_CREATION_REQUESTED: 'agent_creation_requested',
  /** The reader's own agent creation request was confirmed or refused, with the reason (I-1067). */
  AGENT_CREATION_DECIDED: 'agent_creation_decided',
  /**
   * An agent asked for access to a work area, and the reader may answer it (F-0713 slice 2,
   * `concept-work-area-access-requests.md` §4.4). Critical while the request waits; settles to info
   * once it is answered, withdrawn or closed. Carries the facts in `data` (`accessRequestId`, `ref`,
   * the agent, its owner and machine, the work area and the gaps); the requester's words are read
   * from the request, never copied into the notice.
   */
  ACCESS_REQUESTED: 'access_requested',
  /**
   * The reader's agent's access request was answered or closed (F-0713, §4.5): told to the agent
   * and to its owner. Info.
   */
  ACCESS_REQUEST_DECIDED: 'access_request_decided',
  /**
   * An agent's access to a work area ends in seven days (F-0713, D-0413, `concept-work-area-access-
   * requests.md` §7.2): told once, to the agent's owner and to whoever gave it. Info. Carries the
   * agent, the work area and the end in `data`.
   */
  ACCESS_ENDING: 'access_ending',
  /** An agent's access to a work area has ended (F-0713, §7.1): told to the agent, its owner and whoever gave it. Info. */
  ACCESS_ENDED: 'access_ended',
  /**
   * An item the reader holds In progress has gone stale, or the session working on it has stopped
   * (F-0541, `concept-in-progress-holder.md` §3.6). Told once per stale spell. Opens the item.
   */
  HOLDER_STALE: 'holder_stale',
  /**
   * Something a person sends and expects to arrive has stopped arriving (T-0600): a caller getting the
   * same refusal again and again, mail that failed to send, a push stuck in its queue. Told to the
   * deployment's administrators, once per condition. Critical while the condition holds; settles to
   * info when it clears. Not about an item.
   */
  DELIVERY_FAULT: 'delivery_fault',
  /**
   * A session of the reader's has waited for them longer than their threshold, with no subagent
   * working (F-0344, AC-NP5). Once per idle stretch; cleared when the session works again. Opens
   * the session in Agents.
   */
  AGENT_IDLE: 'agent_idle',
  /**
   * Agents of the reader's stopped on a critical error: a usage limit, a refused sign-in, an API
   * error, a crash (F-0575). One notice per group of stops that started together; a later stop
   * inside the window rewrites it rather than adding one. Opens the one session, or Agents.
   */
  AGENT_ERROR: 'agent_error',
  /**
   * Agents of others are blocked on the reader: each handed them one step only they can do (F-0751,
   * `featit hand-off … --blocking`). One notice per person, which every block joins; critical while
   * any of them is open, sent again until opened (BA-3). Opens the latest block's step.
   */
  AGENT_BLOCKED: 'agent_blocked',
  /**
   * A session of the reader's asked for an answer and waits for it (F-0633, AC-CV.35): told once per
   * waiting stretch, cleared when the session moves on. Carries no content: the sentence names the
   * agent and the machine, the data holds ids only. Opens the session.
   */
  AGENT_WAITING: 'agent_waiting',
  /**
   * An item the reader answers for used 80 % of its budget (F-0656 §8, FB-5): once per crossing,
   * to the item's responsible person. Info; reaching 100 % files a decision instead. Opens the item.
   */
  BUDGET_WARNED: 'budget_warned',
  /**
   * F-0815 UTN-1: a task of the reader's became urgent (D-0533 a) — set to urgent, created urgent, or
   * an urgent item made theirs — to its responsible person only. Critical while the item is urgent and
   * open, so its push is time-sensitive (BA-2). Opens the item.
   */
  TASK_URGENT: 'task_urgent',
  /**
   * F-0714 RLY-128: the deployment's identity log names another identity for a machine the reader owns
   * or administers, as that machine's bridge read it: once per entry, on the move into the mismatch.
   * Info (only decisions and blockers are critical, DS-17); the data holds the machine's id and the
   * entry's hash. Opens the machine.
   */
  MACHINE_IDENTITY_MISMATCH: 'machine_identity_mismatch',
  /**
   * F-0794 S8e (PIO-22g): an automatic upgrade of a published package was refused in a work area the
   * reader owns: once per version that failed. Info; the data holds the work area, the package key,
   * the version and each refused component as the install's refusal names it. Opens the package's
   * entry in Settings → Packages.
   */
  PACKAGE_UPGRADE_REFUSED: 'package_upgrade_refused',
  /**
   * A start somebody else made on a machine the reader owns or administers has run there (MP-52c,
   * `concept-machine-permissions.md`): told once per start event, never to the person who started
   * it. The core has no screen, so this is how a headless machine's owner hears of it. Info; the
   * data names the machine, the starter and the folder. Opens the machine.
   */
  MACHINE_START_RAN: 'machine_start_ran',
  /**
   * One of the reader's devices was unpaired from a machine, by themselves or by an administrator
   * (MP-109d.9, `concept-machine-permissions.md`): told once per stored unpairing, on every device
   * the reader is signed in on. Info; the data names the machine, the device and who unpaired it.
   * Opens the machine.
   */
  MACHINE_DEVICE_UNPAIRED: 'machine_device_unpaired',
} as const;

export type NotificationType = (typeof NOTIFICATION_TYPES)[keyof typeof NOTIFICATION_TYPES];

/**
 * One thing that happened on a work item, addressed to one actor.
 *
 * Recipient and actor are both of either kind (ADR-047): exactly one of `userId`/`agentId` is set,
 * and the actor pair carries a display name stamped at write time, so an agent's action never
 * reads as its owner's.
 */
export interface INotification {
  id: string;
  userId: string | null;
  agentId: string | null;
  type: NotificationType;
  /** Null when the notification is about a credential rather than an item. */
  workItemId: string | null;
  credentialId?: string | null;
  /** The delivery fault a Needs-you notice is about (T-0600); it opens that fault's detail. */
  deliveryFaultId?: string | null;
  /** Carries its work area: item keys repeat across work areas, so a key alone cannot be linked. */
  workItem?: {
    key: string;
    title: string;
    project?: { id: string; slug: string; name: string } | null;
    /** Its type: a notice about a decision is Decisions for me's, never a task (I-2382). */
    itemType?: { key: string } | null;
  } | null;
  actorType: string | null;
  actorId: string | null;
  actorAgentId: string | null;
  actorDisplayName: string | null;
  message: string;
  /**
   * The actor as the reader names them today (F-0621), the label `message` begins with; null for a
   * notice that names nobody. A notice worded from its facts (`data`) draws this in its own language.
   */
  actorLabel?: string | null;
  /**
   * `critical` or `info` (F-0206), decided by `notificationSeverityFor` when the row is written.
   * Null on a row from before severity existed, which reads as info (`isCriticalNotification`).
   */
  severity?: string | null;
  /** A question put to the reader (I-1461): its comment, which the notification opens. */
  questionCommentId?: string | null;
  /**
   * The agent sessions an idle or stopped-agent notice is about, as `provider:ref` (F-0344 AC-NP9).
   * `agentNoticePath` turns them into what the notice opens, as its push does. Absent otherwise.
   */
  agentSessionKeys?: string[] | null;
  /**
   * The facts a notice is worded from (I-1714): a plan-limit warning's `IPlanLimitNoticeData`, read
   * by `planLimitNotificationSentence`. Null for other types and for a row written before it.
   */
  data?: unknown;
  isRead: boolean;
  createdAt: string;
}

// Project membership

export const PROJECT_ROLES = {
  VIEWER: 'viewer',
  CONTRIBUTOR: 'contributor',
  MANAGER: 'manager',
  ADMIN: 'admin',
} as const;

export type ProjectRoleType = (typeof PROJECT_ROLES)[keyof typeof PROJECT_ROLES];

/**
 * Another person as a members list shows them (I-0691): a name, a face, and the address people find
 * each other by. Never the whole account: its role, sign-in provider and deactivation are for
 * administrators, and its contact address and delivery standing are the person's own.
 */
export interface IDirectoryPerson {
  id: string;
  name: string;
  email: string;
  avatarUrl: string | null;
  kind: 'user';
}

export interface IProjectMember {
  id: string;
  projectId: string;
  userId: string;
  role: ProjectRoleType;
  user?: IDirectoryPerson;
  createdAt: string;
}
